import { getFormattedNumber } from '@ecg-internal/aurora-js-formatters';

import { TBrandDataDrivenParams, BrandSentenceParameters } from './brandDataDrivenTypes';

// Temporary sentence templates — to be moved to phrase translation before production release.
export const BRAND_DDI_SENTENCES = {
  totalListings:
    '<b>{{brandName}}</b> te koop. Blader van <b>{{firstModel}}</b> tot <b>{{lastModel}}</b> in ons actueel aanbod.',
  topModel:
    'Meest aangeboden model. <b>{{model1}}</b> voert de lijst aan, gevolgd door <b>{{model2}}</b> en <b>{{model3}}</b>.',
  topModel2: 'Meest aangeboden model. <b>{{model1}}</b> voert de lijst aan, gevolgd door <b>{{model2}}</b>.',
  topModel1: 'Meest aangeboden model.',
  avgAge:
    "Gemiddelde leeftijd van aangeboden auto's. De gemiddelde kilometerstand is <b>{{avgMileageKm}} km</b>. <b>{{pctYoungerThan5Years}}%</b> is jonger dan 5 jaar.",
  avgAgeMileage:
    "Gemiddelde leeftijd van aangeboden auto's. De gemiddelde kilometerstand is <b>{{avgMileageKm}} km</b>.",
  avgAgeOnly: "Gemiddelde leeftijd van aangeboden auto's.",
  serviceHistory: 'Gedocumenteerde onderhoudshistorie.',
  dealerSplit: 'Advertenties zijn afkomstig van dealers. De overige <b>{{privatePct}}%</b> zijn van particulieren.',
} as const;

const generateTotalListings = (stats: TBrandDataDrivenParams): BrandSentenceParameters | undefined => {
  const { totalCount, brandName, firstModelAlpha, lastModelAlpha } = stats;
  if (!totalCount || !brandName || !firstModelAlpha || !lastModelAlpha) return undefined;

  const formatted = getFormattedNumber(totalCount);
  return {
    i18nKey: BRAND_DDI_SENTENCES.totalListings,
    heading: formatted,
    totalCount: formatted,
    brandName,
    firstModel: firstModelAlpha,
    lastModel: lastModelAlpha,
  };
};

const generateTopModel = (stats: TBrandDataDrivenParams): BrandSentenceParameters | undefined => {
  const { top3Models } = stats;
  if (!top3Models.length) return undefined;

  const [model1, model2, model3] = top3Models;

  if (top3Models.length >= 3) {
    return { i18nKey: BRAND_DDI_SENTENCES.topModel, heading: model1, model1, model2, model3 };
  }

  if (top3Models.length === 2) {
    return { i18nKey: BRAND_DDI_SENTENCES.topModel2, heading: model1, model1, model2 };
  }

  return { i18nKey: BRAND_DDI_SENTENCES.topModel1, heading: model1, model1 };
};

const generateAvgAge = (stats: TBrandDataDrivenParams): BrandSentenceParameters | undefined => {
  const { avgAgeYears, avgMileageKm, pctYoungerThan5Years } = stats;
  if (avgAgeYears === null) return undefined;

  const ageYears = String(avgAgeYears);

  if (avgMileageKm !== null && pctYoungerThan5Years !== null) {
    return {
      i18nKey: BRAND_DDI_SENTENCES.avgAge,
      heading: `${avgAgeYears} jaar`,
      avgAgeYears: ageYears,
      avgMileageKm: getFormattedNumber(avgMileageKm),
      pctYoungerThan5Years: String(pctYoungerThan5Years),
    };
  }

  if (avgMileageKm !== null) {
    return {
      i18nKey: BRAND_DDI_SENTENCES.avgAgeMileage,
      heading: `${avgAgeYears} jaar`,
      avgAgeYears: ageYears,
      avgMileageKm: getFormattedNumber(avgMileageKm),
    };
  }

  return {
    i18nKey: BRAND_DDI_SENTENCES.avgAgeOnly,
    heading: `${avgAgeYears} jaar`,
    avgAgeYears: ageYears,
  };
};

const generateServiceHistory = (stats: TBrandDataDrivenParams): BrandSentenceParameters | undefined => {
  const { serviceHistoryPct } = stats;
  if (serviceHistoryPct === null) return undefined;

  return {
    i18nKey: BRAND_DDI_SENTENCES.serviceHistory,
    heading: `${serviceHistoryPct}%`,
    serviceHistoryPct: String(serviceHistoryPct),
  };
};

const generateDealerSplit = (stats: TBrandDataDrivenParams): BrandSentenceParameters | undefined => {
  const { dealerPct, privatePct } = stats;
  if (dealerPct === null || privatePct === null) return undefined;

  return {
    i18nKey: BRAND_DDI_SENTENCES.dealerSplit,
    heading: `${dealerPct}%`,
    dealerPct: String(dealerPct),
    privatePct: String(privatePct),
  };
};

const GENERATORS = [
  generateTotalListings,
  generateTopModel,
  generateAvgAge,
  generateServiceHistory,
  generateDealerSplit,
];

const generateBrandDataDrivenSentences = (stats: TBrandDataDrivenParams): BrandSentenceParameters[] =>
  GENERATORS.map((fn) => fn(stats)).filter(Boolean) as BrandSentenceParameters[];

export default generateBrandDataDrivenSentences;
